import React, { useEffect, useState } from "react";
import { Button, Flex, Progress, Select, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { employees, getEmployeeName } from "../../Config/Employees";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsCard } from "../../components/Hrms/HrmsCard";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { HrmsStatCard } from "../../components/Hrms/HrmsStatCard";
import { PersonCell } from "../../components/Hrms/hrmsUi";

interface EmployeeAttendanceSummary {
  email: string;
  name: string;
  fullDays: number;
  halfDays: number;
  absentDays: number;
  punchMissingDays: number;
  lateDays: number;
  earlyExitDays: number;
  overtimeMinutes: number;
}

export const AttendanceReports = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const { canViewReports } = getUserAccess();

  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [month, setMonth] = useState(DateTime.now().toFormat("MM"));
  const [year, setYear] = useState(DateTime.now().year.toString());
  const [selectedEmail, setSelectedEmail] = useState("");
  const [workingDays, setWorkingDays] = useState(0);
  const [rows, setRows] = useState<EmployeeAttendanceSummary[]>([]);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getAttendanceReport(
        token,
        month,
        year,
        userEmail,
        selectedEmail,
      );
      if (res.data.status === "success" && res.data.data) {
        setWorkingDays(res.data.data.workingDays || 0);
        setRows(res.data.data.employees || []);
      }
    } catch (e: any) {
      console.error("Error fetching attendance report:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const res = await EmployeeDataService.exportAttendanceReport(
        token,
        month,
        year,
        userEmail,
        selectedEmail,
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attendance-report-${month}-${year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      console.error("Error exporting attendance report:", e);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Days present (half days count as 0.5) out of the period's working days.
  const attendanceRate = (row: EmployeeAttendanceSummary) =>
    workingDays ? Math.min(1, (row.fullDays + row.halfDays * 0.5) / workingDays) : 0;

  return (
    <HrmsPageShell
      title="Attendance Reports"
      description="Monthly attendance summary per employee"
      sectionKey="reports"
      noPermission={!canViewReports}
    >
      <HrmsCard mb={4}>
        <Flex gap={4} wrap="wrap" align="flex-end">
          {canViewReports && (
            <Select
              placeholder="All Employees"
              w="220px"
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
            >
              {employees.map((emp) => (
                <option key={emp.value} value={emp.value}>
                  {emp.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            w="140px"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {Array.from({ length: 12 }, (_, i) =>
              String(i + 1).padStart(2, "0"),
            ).map((m) => (
              <option key={m} value={m}>
                {DateTime.fromObject({ month: Number(m) }).toFormat("MMMM")}
              </option>
            ))}
          </Select>
          <Select w="120px" value={year} onChange={(e) => setYear(e.target.value)}>
            {[year, String(Number(year) - 1)].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
          <Button
            bg="#327171"
            color="white"
            _hover={{ bg: "#285e5e" }}
            onClick={fetchReport}
          >
            Generate
          </Button>
          <Button
            variant="outline"
            borderColor="#327171"
            color="#327171"
            onClick={handleExport}
            isLoading={isExporting}
          >
            Export to Excel
          </Button>
        </Flex>

        <Text fontSize="13px" color="gray.500" mt={3} mb={0}>
          Working days in period (excludes weekends and configured holidays):{" "}
          {workingDays}
        </Text>
      </HrmsCard>

      {isLoading ? (
        <Flex justify="center" align="center" h="40vh">
          <Spinner size="xl" color="#308888" />
        </Flex>
      ) : (
        <>
        {rows.length > 0 && (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
            <HrmsStatCard label="Employees" value={rows.length} />
            <HrmsStatCard
              label="Avg. attendance"
              value={`${Math.round(
                (rows.reduce((sum, r) => sum + attendanceRate(r), 0) / rows.length) * 100,
              )}%`}
              helpText={`of ${workingDays} working days`}
            />
            <HrmsStatCard
              label="Late arrivals"
              value={rows.reduce((sum, r) => sum + r.lateDays, 0)}
              accent="#dd6b20"
            />
            <HrmsStatCard
              label="Overtime"
              value={`${Math.floor(rows.reduce((sum, r) => sum + r.overtimeMinutes, 0) / 60)}h`}
            />
          </SimpleGrid>
        )}
        <HrmsTable
          head={[
            "Employee",
            "Attendance",
            "Full",
            "Half",
            "Absent",
            "Log-out missing",
            "Late",
            "Early exit",
            "Overtime",
          ]}
          isEmpty={rows.length === 0}
          emptyText="No attendance data for this period"
        >
          {rows.map((row) => (
            <tr key={row.email}>
              <td>
                <PersonCell
                  name={row.name || getEmployeeName(row.email) || row.email}
                  subtitle={row.email}
                />
              </td>
              <td>
                <Flex align="center" gap={2} minW="140px">
                  <Progress
                    value={attendanceRate(row) * 100}
                    size="sm"
                    borderRadius="full"
                    colorScheme={attendanceRate(row) >= 0.9 ? "green" : attendanceRate(row) >= 0.7 ? "orange" : "red"}
                    flex={1}
                  />
                  <Text as="span" fontSize="12px" fontWeight="600" w="38px" textAlign="right">
                    {Math.round(attendanceRate(row) * 100)}%
                  </Text>
                </Flex>
              </td>
              <td>{row.fullDays}</td>
              <td>{row.halfDays}</td>
              <td style={{ color: row.absentDays ? "#c53030" : undefined }}>{row.absentDays}</td>
              <td style={{ color: row.punchMissingDays ? "#c05621" : undefined }}>{row.punchMissingDays ?? 0}</td>
              <td style={{ color: row.lateDays ? "#c05621" : undefined }}>{row.lateDays}</td>
              <td style={{ color: row.earlyExitDays ? "#c05621" : undefined }}>{row.earlyExitDays}</td>
              <td>
                {Math.floor(row.overtimeMinutes / 60)}h{" "}
                {row.overtimeMinutes % 60}m
              </td>
            </tr>
          ))}
        </HrmsTable>
        </>
      )}
    </HrmsPageShell>
  );
};
